import { useEffect, useState } from 'react';

/** Chrome/Edge/Android's install event (not in the DOM lib types). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// The browser fires this once, often before React mounts, so capture it at
// module load and hand it to whichever component asks later.
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we show our own button instead of the mini-infobar
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => { deferred = null; notify(); });
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * How this device can install the app:
 * - 'prompt'   — Chrome/Edge/Android: install() opens the browser's dialog
 * - 'ios'      — iPhone/iPad Safari: no API, show "Share → Add to Home Screen"
 * - 'installed'/'unavailable' — nothing to offer
 */
export function useInstall() {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const mode: 'prompt' | 'ios' | 'installed' | 'unavailable' =
    isStandalone() ? 'installed' : deferred ? 'prompt' : ios ? 'ios' : 'unavailable';

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    deferred = null; // single-use
    notify();
  }

  return { mode, install };
}
